// Figma: no frame provided for this piece — built using the established design system
// (tokens, the Badge.js pill visual pattern, card conventions from CampaignInfo.js/
// AlertsSection.js) per the "extrapolate" decision.
// LOGIC PORTED from LC2's commons/components/Offers/OffersPlanner.js:
// - The three-column Expired/Active/Upcoming layout, each showing the first 3 offers with a
//   "+N more" expand toggle, is ported as-is (`OffersSorted`/`OfferSection` -> `PlannerColumn`
//   below).
// - The editability rule is ported verbatim: an offer's edit/delete affordances only show when
//   `isEditable` for that section (Expired requires `getIsStakeAdmin`; Active/Upcoming are
//   always editable) AND (`offerDeterminersValues.length == 1` OR (`length > 1` AND not a
//   legacy offer)) AND `canEdit` (an offer on the "Getting current" action can only be edited by
//   a stake.rent teammate, via `getEmail`).
// - "Offer was created/updated on <date> by <team member>" footer is ported verbatim,
//   including the real `useLazyGetTeamMemberInfoByEmailOrIdQuery` lookup for created/updated by.
// SCOPE CUT (per task): LC2's New Offer / Edit / Delete buttons open modals
// (NewOfferModal.js, EditOfferModal.js, DeleteOfferModal.js) that are out of scope for this
// read-only port. They're rendered here as visually real, disabled-looking, inert
// controls — `onClick` does nothing, and hovering explains why (see the `// TODO:` comments
// at each button below).
// SCOPE CUT: LC2's `ContactListInfoComponent` (Offers/ContactLists.js) — the "which contact
// list residents fall into" panel — is dropped entirely rather than stubbed. It's a full
// sub-feature (its own DataGridPro-backed table + accordion, own API calls), not a single
// button, so it's out of scope rather than an inert stand-in.
import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import {Box, Button, Stack, Tooltip, Typography} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {getEmail, getIsStakeAdmin} from '../../../app/store/reducers/session';
import {useLazyGetTeamMemberInfoByEmailOrIdQuery} from '../../../features/loyaltyCloudApi/apiSlice';
import {activeColorsObj} from '../../../commons/constants/offersConstants';
import {tokens} from '../../../themes/ThemeCustomization';

// Small colored pill, following Badge.js's visual pattern (999px radius, 24px height, caption
// label) but taking an arbitrary background/text color pair since offer colors are data-driven
// (one of ~10 `activeOffersColors` entries, not a fixed enum Badge's VARIANTS map models).
const OfferPill = ({label, bg, color}) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '24px',
            px: 1,
            borderRadius: '999px',
            backgroundColor: bg,
            maxWidth: '100%'
        }}
    >
        <Typography variant="caption" fontWeight={500} sx={{color, whiteSpace: 'normal', lineHeight: 1.4}}>
            {label}
        </Typography>
    </Box>
);

OfferPill.propTypes = {label: PropTypes.node, bg: PropTypes.string, color: PropTypes.string};

const PillGroup = ({items, mainColor}) => {
    if (!items || items.length === 0) return null;
    const textColor = activeColorsObj[mainColor] || tokens.color.neutral700;
    return (
        <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={1} rowGap={0.5}>
            {items.map((item, idx) => (
                <Stack key={idx} direction="row" alignItems="center" columnGap={1}>
                    <OfferPill label={item} bg={mainColor} color={textColor} />
                    {idx !== items.length - 1 && (
                        <Typography variant="caption" sx={{color: textColor}}>
                            &amp;
                        </Typography>
                    )}
                </Stack>
            ))}
        </Stack>
    );
};

PillGroup.propTypes = {items: PropTypes.array, mainColor: PropTypes.string};

const EmptySection = ({title}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            border: `1px dashed ${tokens.color.neutral200}`,
            borderRadius: '8px'
        }}
    >
        <Typography variant="caption" sx={{color: tokens.color.neutral500, textAlign: 'center'}}>
            No {title.toLowerCase()} offer
        </Typography>
    </Box>
);

EmptySection.propTypes = {title: PropTypes.string};

const OfferInfoFooter = ({rawAction, mainColor}) => {
    const createdAt = rawAction?.created_at ? dayjs(rawAction.created_at).format('MMM DD, YYYY') : null;
    const updatedAt = rawAction?.updated_at ? dayjs(rawAction.updated_at).format('MMM DD, YYYY') : null;
    const createdBy = rawAction?.created_by_team_member_id;
    const updatedBy = rawAction?.updated_by_team_member_id;

    const [createdByInfo, setCreatedByInfo] = useState(null);
    const [updatedByInfo, setUpdatedByInfo] = useState(null);
    const [fetchTeamMemberByEmail] = useLazyGetTeamMemberInfoByEmailOrIdQuery();

    useEffect(() => {
        const fetchMemberInfo = async (email, setterFunction) => {
            if (email) {
                try {
                    const result = await fetchTeamMemberByEmail({email, teamMemberId: null}).unwrap();
                    setterFunction(result);
                } catch (error) {
                    console.error(`Error fetching member info for ID ${email}:`, error);
                }
            }
        };
        if (createdBy) fetchMemberInfo(createdBy, setCreatedByInfo);
        if (updatedBy) fetchMemberInfo(updatedBy, setUpdatedByInfo);
    }, [createdBy, updatedBy, fetchTeamMemberByEmail]);

    const createText = useMemo(() => {
        let text = `on ${createdAt}`;
        if (createdByInfo?.first_name && createdByInfo?.last_name) {
            text += ` by ${createdByInfo.first_name} ${createdByInfo.last_name}`;
        }
        return text;
    }, [createdAt, createdByInfo]);

    const updateText = useMemo(() => {
        let text = '';
        if (updatedAt && updatedAt !== createdAt) text = `on ${updatedAt}`;
        if (updatedByInfo?.first_name && updatedByInfo?.last_name) {
            text += ` by ${updatedByInfo.first_name} ${updatedByInfo.last_name}`;
        }
        return text;
    }, [updatedAt, updatedByInfo, createdAt]);

    const textColor = activeColorsObj[mainColor] || tokens.color.neutral700;

    return (
        <Box
            sx={{
                backgroundColor: mainColor,
                borderRadius: '0 0 8px 8px',
                p: 1.5,
                width: '100%'
            }}
        >
            <Typography variant="caption" sx={{color: textColor, display: 'block'}}>
                Offer was created {createText}
            </Typography>
            {updateText && (
                <Typography variant="caption" sx={{color: textColor, display: 'block'}}>
                    Offer was last updated {updateText}
                </Typography>
            )}
        </Box>
    );
};

OfferInfoFooter.propTypes = {rawAction: PropTypes.object, mainColor: PropTypes.string};

const OfferCard = ({offer, mainColor, isEditable, offerStatus}) => {
    const email = useSelector(getEmail);
    const isGetCurrent = offer?.rawAction?.action_name === 'Getting current';
    const canEdit = !isGetCurrent || (isGetCurrent && (email || '').includes('stake.rent'));

    const showEditControls =
        (isEditable && offer.offerDeterminersValues.length === 1 && canEdit) ||
        (isEditable && offer.offerDeterminersValues.length > 1 && !offer.rawAction.isLegacy && canEdit);

    const rewardChips =
        offer.customSchedulesChipValues && offer.customSchedulesChipValues.length > 0
            ? offer.customSchedulesChipValues
            : [offer.offerRewardChipValue];

    return (
        <Stack direction="column" width="100%">
            <Box
                sx={{
                    border: `1px solid ${activeColorsObj[mainColor] || tokens.color.neutral200}`,
                    borderRadius: '8px 8px 0 0',
                    borderBottom: 'none',
                    p: 1.5,
                    width: '100%'
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" width="100%">
                    <Stack direction="column" rowGap={0.75} width={showEditControls ? '80%' : '100%'}>
                        <Typography variant="caption" fontWeight={600}>
                            Residents who
                        </Typography>
                        <PillGroup items={offer.offerDeterminersValues} mainColor={mainColor} />

                        {offer.offersTargets && offer.offersTargets.length > 0 && (
                            <>
                                <Typography variant="caption" fontWeight={600}>
                                    with lease minimum
                                </Typography>
                                <PillGroup items={offer.offersTargets} mainColor={mainColor} />
                            </>
                        )}

                        {offer.brTargets && offer.brTargets.length > 0 && (
                            <>
                                <Typography variant="caption" fontWeight={600}>
                                    for unit floor plans
                                </Typography>
                                <PillGroup items={offer.brTargets} mainColor={mainColor} />
                            </>
                        )}

                        <Typography variant="caption" fontWeight={600}>
                            earn
                        </Typography>
                        <PillGroup items={rewardChips} mainColor={mainColor} />
                    </Stack>
                    {showEditControls && (
                        <Stack direction="column" rowGap={1}>
                            {/* TODO: opens LC2's Offers/EditOfferModal.js — out of scope, stubbed inert */}
                            <Tooltip title="Editing offers is coming soon" arrow>
                                <span>
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => {}}
                                        aria-label={`Edit ${offerStatus} offer`}
                                        sx={{
                                            border: 'none',
                                            background: 'none',
                                            p: 0.5,
                                            cursor: 'pointer',
                                            color: tokens.color.neutral500,
                                            display: 'flex'
                                        }}
                                    >
                                        <EditOutlinedIcon fontSize="small" />
                                    </Box>
                                </span>
                            </Tooltip>
                            {/* TODO: opens LC2's Offers/DeleteOfferModal.js — out of scope, stubbed inert */}
                            <Tooltip title="Deleting offers is coming soon" arrow>
                                <span>
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => {}}
                                        aria-label={`Delete ${offerStatus} offer`}
                                        sx={{
                                            border: 'none',
                                            background: 'none',
                                            p: 0.5,
                                            cursor: 'pointer',
                                            color: tokens.color.neutral500,
                                            display: 'flex'
                                        }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </Box>
                                </span>
                            </Tooltip>
                        </Stack>
                    )}
                </Stack>
            </Box>
            <OfferInfoFooter rawAction={offer.rawAction} mainColor={mainColor} />
        </Stack>
    );
};

OfferCard.propTypes = {
    offer: PropTypes.object,
    mainColor: PropTypes.string,
    isEditable: PropTypes.bool,
    offerStatus: PropTypes.string
};

const PlannerColumn = ({title, offers, isEditable}) => {
    const [isExtended, setExtended] = useState(false);
    const visibleOffers = offers.slice(0, 3);
    const extraOffers = offers.slice(3);

    return (
        <Stack direction="column" rowGap={2} sx={{flex: 1, minWidth: 0}}>
            <Typography variant="subtitle2" fontWeight={600} sx={{textAlign: 'center', color: tokens.color.neutral700}}>
                {title}
            </Typography>
            <Stack direction="column" rowGap={1.5}>
                {visibleOffers.length === 0 && <EmptySection title={title} />}
                {visibleOffers.map((offer, idx) => (
                    <OfferCard key={`visible-${idx}`} offer={offer} mainColor={offer.color} isEditable={isEditable} offerStatus={title} />
                ))}
                {isExtended &&
                    extraOffers.map((offer, idx) => (
                        <OfferCard key={`extra-${idx}`} offer={offer} mainColor={offer.color} isEditable={isEditable} offerStatus={title} />
                    ))}
                {extraOffers.length > 0 && !isExtended && (
                    <Box display="flex" justifyContent="center">
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setExtended(true)}
                            sx={{fontSize: '0.75rem', height: 28, px: 1.5}}
                        >
                            +{extraOffers.length} more
                        </Button>
                    </Box>
                )}
            </Stack>
        </Stack>
    );
};

PlannerColumn.propTypes = {
    title: PropTypes.string,
    offers: PropTypes.array,
    isEditable: PropTypes.bool
};

const OfferPlanner = ({offersObj, campaign}) => {
    const {pastOffers = [], currentOffers = [], futureOffers = []} = offersObj || {};
    const isStakeAdmin = useSelector(getIsStakeAdmin);

    const isDraft = campaign?.status === 'Draft';
    const canCreateOffer = campaign?.subtype && !campaign.subtype.toLowerCase().includes('stake1');

    let newOfferButton = null;
    if (isDraft) {
        newOfferButton = (
            <Tooltip title="Please speak with your Stake Customer Success manager to launch your campaign before creating your offer" arrow>
                <span>
                    <Button variant="contained" color="secondary" disabled endIcon={<AddCircleOutlineIcon fontSize="small" />}>
                        New Offer
                    </Button>
                </span>
            </Tooltip>
        );
    } else if (canCreateOffer) {
        // TODO: opens LC2's Offers/NewOfferModal.js (which itself composes GoalChoosingPart,
        // FillingOtherOfferParts, CustomRewradSchedule) — out of scope, stubbed inert.
        newOfferButton = (
            <Tooltip title="Creating offers is coming soon" arrow>
                <span>
                    <Button variant="contained" color="secondary" onClick={() => {}} endIcon={<AddCircleOutlineIcon fontSize="small" />}>
                        New Offer
                    </Button>
                </span>
            </Tooltip>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                p: 2,
                border: `1px solid ${tokens.color.neutral200}`,
                borderRadius: tokens.radius.card,
                backgroundColor: tokens.color.white
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Offer Planner
                </Typography>
                {newOfferButton}
            </Stack>
            <Stack direction="row" columnGap={2} sx={{width: '100%'}}>
                <PlannerColumn title="Expired" offers={[...pastOffers]} isEditable={isStakeAdmin} />
                <PlannerColumn title="Active" offers={[...currentOffers]} isEditable={true} />
                <PlannerColumn title="Upcoming" offers={[...futureOffers]} isEditable={true} />
            </Stack>
        </Box>
    );
};

OfferPlanner.propTypes = {
    offersObj: PropTypes.object,
    campaign: PropTypes.object
};

export default OfferPlanner;
